import React, { ReactElement, useContext } from 'react'
import moment from 'moment'
import {
  PixelBreadcrumbs,
  PixelButton,
  PixelDropDown,
  PixelFlexBox,
  PixelPageContainer,
  PixelTable,
  PixelTag,
  PixelText,
  PixelTopBar,
  Section,
  SectionContent,
  SectionTitle,
} from 'pixel-base'
import styled from 'styled-components'

import {
  exportReconciliationReport,
  getGeneralCourierList,
  getMonthWiseReconciliationDetail,
  getMonthWiseReconciliationRecord,
} from 'utils/api-routes.util'
import { SectionActions, SectionHeader } from 'app/common/styles/section'

import { IReconciliationHeader } from '../ReconciliationMonthWise'

interface IData {
  data: any[]
}

interface AjaxResponse<T> {
  response: {
    data: T
  }
}

const breadcrumbs = [
  {
    name: 'Payment Reconciliation',
    active: false,
    link: '/analytics/reconciliation-month-wise',
  },
  {
    name: 'Details',
    active: true,
  },
]

interface IMonthWiseDetail {
  id: number
  ems_customer_id: number
  courier_id: number
  order_id: number
  tracking_id: string
  source_order_id: string
  courier_cod: number | null
  pixelone_cod: string | null
  shipment_charges: number | null
  other_charges: number | null
  courier_status: string | null
  pixelone_status: string | null
  reconciliation_status: string
  header_id: number
}

interface IFilters {
  reconcile_status: string
  courier_id: number
}

interface LoaderContext {
  showLoader: () => void
  hideLoader: () => void
}

const ReconciliationMonthWiseDetail: React.FC = () => {
  const searchParams = new URLSearchParams(window.location.search)
  const [tableLoading, setTableLoading] = React.useState(false)
  const [monthWiseDetail, setMonthWiseDetail] = React.useState<
    IMonthWiseDetail[]
  >([])
  const [monthWiseSummary, setMonthWiseSummary] = React.useState<
    IReconciliationHeader[]
  >([])
  const [courierList, setCourierList] = React.useState([])
  const headerRef = React.useRef<ReactElement[]>(null)
  const loaderContext = useContext(LoaderContext) as LoaderContext

  const ExpandedComponent = ({ data }) => {
    return (
      <PixelFlexBox
        flexDirection="column"
        style={{
          padding: '10px 10px',
          border: '1px solid #e0e0e0',
        }}
      >
        <PixelText textSize="15px" style={{ paddingBottom: '3px' }}>
          More Information ({data.courier_name})
        </PixelText>
        <PixelFlexBox
          flexDirection="column"
          style={{
            paddingLeft: '10px',
          }}
        >
          <PixelText textSize="13px" customColor="#000000de">
            Courier COD :&nbsp;
            <strong>{data.courier_cod || '--'}</strong>
          </PixelText>
          <PixelText textSize="13px" customColor="#000000de">
            PixelOne COD :&nbsp;
            <strong>{data.pixelone_cod || '--'}</strong>
          </PixelText>
          <PixelText textSize="13px" customColor="#000000de">
            Courier Status :&nbsp;
            <strong>{data.courier_status || '--'}</strong>
          </PixelText>
          <PixelText textSize="13px" customColor="#000000de">
            PixelOne Status :&nbsp;
            <strong>{data.pixelone_status || '--'}</strong>
          </PixelText>
          {data.shipment_charges && (
            <PixelText textSize="13px" customColor="#000000de">
              Shipment Charges :&nbsp;
              <strong>{data.shipment_charges || '--'}</strong>
            </PixelText>
          )}
          {data.other_charges && (
            <PixelText textSize="13px" customColor="#000000de">
              Other Charges :&nbsp;
              <strong>{data.other_charges || '--'}</strong>
            </PixelText>
          )}
        </PixelFlexBox>
      </PixelFlexBox>
    )
  }

  const [filters, setFilters] = React.useState<IFilters>({
    reconcile_status: 'All',
    courier_id: 'All',
    month: searchParams.get('month'),
    year: searchParams.get('year'),
  })

  const [summaryFilters, setSummaryFilters] = React.useState({
    month: searchParams.get('month'),
    year: searchParams.get('year'),
    courier_id: 'All',
  })

  const getMonthWiseSummary = (): void => {
    loaderContext.showLoader(headerRef)
    getMonthWiseReconciliationRecord(summaryFilters).subscribe(
      (res: AjaxResponse<IData>) => {
        setMonthWiseSummary(res.response.data)
        loaderContext.hideLoader(headerRef)
      },
    )
  }

  const getMonthWiseDetails = (): void => {
    setTableLoading(true)
    getMonthWiseReconciliationDetail(filters).subscribe(
      (res: AjaxResponse<IData>) => {
        setMonthWiseDetail(res.response?.data)
        setTableLoading(false)
      },
    )
  }
  const fetchAllCouriers = () => {
    getGeneralCourierList().subscribe((res: AjaxResponse<IData>) => {
      setCourierList(res.response.data)
    })
  }

  React.useEffect(() => {
    getMonthWiseSummary()
    fetchAllCouriers()
  }, [])

  React.useEffect(() => {
    getMonthWiseSummary()
    getMonthWiseDetails()
  }, [filters])

  const exportReport = () => {
    exportReconciliationReport(filters)
  }

  const columns = [
    {
      dataKey: 'source_order_id',
      name: 'Source Id',
      sortable: true,
      selector: (row) => <PixelText> {row.source_order_id || '--'} </PixelText>,
    },
    {
      dataKey: 'order_id',
      name: 'Order Id',
      sortable: true,
      selector: (row) => <PixelText> {row.order_id || '--'} </PixelText>,
    },
    {
      dataKey: 'tracking_id',
      name: 'Tracking Id',
      selector: (row) => <PixelText> {row.tracking_id || '--'} </PixelText>,
    },
    {
      dataKey: 'reconciliation_status',
      name: 'Reconciliation Status',
      selector: (row) => (
        <PixelText> {row.reconciliation_status || '--'} </PixelText>
      ),
    },
  ]

  return (
    <>
      <PixelTopBar>
        <PixelBreadcrumbs data={breadcrumbs} />
        <SectionActions>
          <PixelButton
            onClick={() => {
              exportReport()
            }}
            size="sm"
          >
            Export
          </PixelButton>
        </SectionActions>
      </PixelTopBar>
      <PixelPageContainer>
        <Section>
          <SectionHeader>
            <PixelFlexBox alignItems={'center'}>
              <PixelFlexBox justifyContent={'flex-start'}>
                <SectionTitle>Summary </SectionTitle>
              </PixelFlexBox>
              <PixelFlexBox justifyContent={'flex-end'}>
                <PixelFlexBox>
                  <PixelFlexBox
                    gap={'10px'}
                    flexDirection={'column'}
                    height={'auto'}
                    style={{ marginBottom: '10px', padding: '10px' }}
                    justifyContent={'flex-start'}
                  >
                    <PixelDropDown
                      placeholder="Select Status"
                      style={{ backgroundColor: '#f6f6f6' }}
                      options={[
                        {
                          label: 'All',
                          value: 'All',
                        },
                        {
                          label: (
                            <PixelTag customBackgroundColor={'green'}>
                              Reconciled
                            </PixelTag>
                          ),
                          value: 'reconciled',
                        },
                        {
                          label: (
                            <PixelTag customBackgroundColor={'red'}>
                              Discrepant{' '}
                            </PixelTag>
                          ),
                          value: 'discrepant',
                        },
                        {
                          label: (
                            <PixelTag customBackgroundColor={'#0052cc'}>
                              In Progress{' '}
                            </PixelTag>
                          ),
                          value: 'inprogress',
                        },
                      ]}
                      value={filters.reconcile_status || 'All'}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          reconcile_status: e.target.value,
                        })
                      }
                    />
                  </PixelFlexBox>
                  <PixelFlexBox
                    gap={'10px'}
                    flexDirection={'column'}
                    height={'auto'}
                    style={{ marginBottom: '10px', padding: '10px' }}
                    justifyContent={'flex-start'}
                  >
                    <PixelDropDown
                      placeholder="Select Courier"
                      style={{ backgroundColor: '#f6f6f6' }}
                      options={[
                        {
                          courier_name: 'All',
                          courier_id: 'All',
                        },
                        ...courierList,
                      ].map((item) => {
                        return {
                          label: item?.courier_name,
                          value: item?.courier_id,
                        }
                      })}
                      value={filters.courier_id || 'All'}
                      onChange={(e) => {
                        setFilters({
                          ...filters,
                          courier_id: e.target.value,
                        })
                        setSummaryFilters({
                          ...summaryFilters,
                          courier_id: e.target.value,
                        })
                      }}
                    />
                  </PixelFlexBox>
                </PixelFlexBox>
              </PixelFlexBox>
            </PixelFlexBox>
          </SectionHeader>
          <SectionContent ref={headerRef}>
            <InfoItem>
              <PixelText customColor="#8e959b" fontSize="16px">
                <PixelFlexBox
                  style={{ fontWeight: '500' }}
                  padding="10px 0 0 0"
                >
                  Month :
                </PixelFlexBox>
              </PixelText>
              <Detail>
                <PixelText textSize="15px">
                  {monthWiseSummary?.month_ && monthWiseSummary?.year_
                    ? moment({
                        year: monthWiseSummary?.year_,
                        month: monthWiseSummary?.month_ - 1,
                      }).format('MMMM YYYY')
                    : '--'}
                </PixelText>
              </Detail>
            </InfoItem>
            <InfoItem>
              <PixelText customColor="#8e959b" fontSize="16px">
                <PixelFlexBox
                  style={{ fontWeight: '500' }}
                  padding="10px 0 0 0"
                >
                  Reconciled :
                </PixelFlexBox>
              </PixelText>
              <Detail>
                <PixelText textSize="15px">
                  {monthWiseSummary?.reconciled
                    ? monthWiseSummary?.reconciled
                    : 0}
                </PixelText>
              </Detail>
            </InfoItem>
            <InfoItem>
              <PixelText customColor="#8e959b" fontSize="16px">
                <PixelFlexBox
                  style={{ fontWeight: '500' }}
                  padding="10px 0 0 0"
                >
                  Discrepant :
                </PixelFlexBox>
              </PixelText>
              <Detail>
                <PixelText textSize="15px">
                  {monthWiseSummary?.discrepant
                    ? monthWiseSummary?.discrepant
                    : 0}
                </PixelText>
              </Detail>
            </InfoItem>
            <InfoItem>
              <PixelText customColor="#8e959b" fontSize="16px">
                <PixelFlexBox
                  style={{ fontWeight: '500' }}
                  padding="10px 0 0 0"
                >
                  InProgress :
                </PixelFlexBox>
              </PixelText>
              <Detail>
                <PixelText textSize="15px">
                  {monthWiseSummary?.inprogress
                    ? monthWiseSummary?.inprogress
                    : 0}
                </PixelText>
              </Detail>
            </InfoItem>
            <InfoItem>
              <PixelText customColor="#8e959b" fontSize="16px">
                <PixelFlexBox
                  style={{ fontWeight: '500' }}
                  padding="10px 0 0 0"
                >
                  Total Orders :
                </PixelFlexBox>
              </PixelText>
              <Detail>
                <PixelText textSize="15px">
                  {monthWiseSummary?.total_orders
                    ? monthWiseSummary?.total_orders
                    : 0}
                </PixelText>
              </Detail>
            </InfoItem>
          </SectionContent>
          <SectionTitle style={{ marginTop: '20px' }}>Details</SectionTitle>
          <SectionContent>
            <PixelTable
              columns={columns}
              data={monthWiseDetail}
              progressPending={tableLoading}
              expandableRows
              expandableRowsComponent={ExpandedComponent}
              expandOnRowClicked
              highlightOnHover
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>
    </>
  )
}

const InfoItem = styled.div`
  display: inline-table;
  table-layout: fixed;
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;

  &:last-child {
    border-bottom: none;
  }
`
const Detail = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #000;
  display: table-cell;
  vertical-align: middle;
  padding: 10px 10px 10px 20px;
  width: 60%;
  background-color: #e9ecef78;
`

export default ReconciliationMonthWiseDetail
